import { useState } from "react";

import { Box, Flex, useDisclosure } from "@chakra-ui/react";
import { useIntl } from "react-intl";
import { useNavigate } from "react-router";

import { Asset, AssetState } from "__graphql__/types";
import { AssetCountEventType, AssetCountStatus } from "analytics/events";
import { routes } from "config/routes";
import { Page } from "shared/components/Page";
import { useAnalytics } from "shared/hooks/useAnalytics";
import { useCustomToast } from "shared/hooks/useCustomToast";
import { Button } from "ui/Button/Button";
import { NavigationHeader } from "ui/NavigationHeader/NavigationHeader";
import { TitleS } from "ui/Typography/Typography";

import {
  BatteryReportDropdown,
  BatteryDamagedModal,
  ReportBatteryLostModal,
  BatteryFunctionalModal,
} from "../components";
import { useReportMissingBarcode } from "../hooks/useReportMissingBarcode";
import { useUpdateAssetStateMutation } from "../queries/assets.generated";
import { useFinishRecurrentTaskMutation } from "../queries/tasks.generated";
import { useAssetManagementStore } from "../stores/assetManagementStore";
import { ASSET_TYPE } from "../utils/constants";
import { serialiseReportedAssets } from "../utils/outboundAssets";

type ModalType = "damage" | "lost" | "functional";

export function BatteryCountMissingPage() {
  const intl = useIntl();
  const navigate = useNavigate();
  const { showToastUI } = useCustomToast();
  const { sendSegmentTrackEvent } = useAnalytics();
  const {
    missingAssets,
    reportedAssets,
    countedAssets,
    currentTaskId,
    countSessionId,
    setReportedAsset,
    removeReportedAsset,
    addCountedAsset,
    removeCountedAsset,
    reset: resetStore,
  } = useAssetManagementStore();

  const [selectedReasons, setSelectedReasons] = useState<Record<string, AssetState | null>>({});
  const [reasonToConfirm, setReasonToConfirm] = useState<AssetState | null>(null);
  const [assetIdToConfirm, setAssetIdToConfirm] = useState<string | null>(null);

  const [updateAssetState, { loading: isUpdatingAssetState }] = useUpdateAssetStateMutation();
  const [finishRecurrentTask, { loading: isFinishingRecurrentTask }] =
    useFinishRecurrentTaskMutation();
  const { reportMissingBarcode, isReportingMissingBarcode } = useReportMissingBarcode();

  const {
    isOpen: isBatteryDamagedModalOpen,
    onOpen: openBatteryDamagedModal,
    onClose: closeBatteryDamagedModal,
  } = useDisclosure();
  const {
    isOpen: isReportBatteryLostModalOpen,
    onOpen: openReportBatteryLostModal,
    onClose: closeReportBatteryLostModal,
  } = useDisclosure();
  const {
    isOpen: isBatteryFunctionalModalOpen,
    onOpen: openBatteryFunctionalModal,
    onClose: closeBatteryFunctionalModal,
  } = useDisclosure();

  const openControlByModal: Record<ModalType, () => void> = {
    damage: openBatteryDamagedModal,
    lost: openReportBatteryLostModal,
    functional: openBatteryFunctionalModal,
  };
  const closeControlByModal: Record<ModalType, () => void> = {
    damage: closeBatteryDamagedModal,
    lost: closeReportBatteryLostModal,
    functional: closeBatteryFunctionalModal,
  };

  const handleOpenModalToConfirm = (modal: ModalType, assetId: string, newReason: AssetState) => {
    setReasonToConfirm(newReason);
    setAssetIdToConfirm(assetId);
    openControlByModal[modal]();
  };

  const allReasonsSelected =
    missingAssets.length > 0 && missingAssets.every((asset) => selectedReasons[asset.id]);

  const updateWithNewState = async (assetId: string, newState: AssetState) => {
    try {
      await updateAssetState({
        variables: {
          input: {
            assetId,
            newState: newState!,
          },
        },
      });
      showToastUI({
        status: "success",
        id: "battery_count_success",
        title: "pages.inventory.battery-count-missing-page.update-state-toast.success",
      });
      setReportedAsset({ id: assetId, state: newState });
      removeCountedAsset(assetId);
      setSelectedReasons((prev) => ({ ...prev, [assetId]: newState }));
    } catch (error) {
      showToastUI({
        status: "error",
        id: "battery_count_error",
        title: "pages.inventory.battery-count-missing-page.update-state-toast.error",
      });
    }
  };

  const handleConfirmReason = () => {
    if (!assetIdToConfirm || !reasonToConfirm) return;
    updateWithNewState(assetIdToConfirm, reasonToConfirm);
  };

  const handleModalConfirm = (modal: ModalType) => () => {
    handleConfirmReason();
    closeControlByModal[modal]();
  };

  const handleModalCancel = (modal: ModalType) => () => {
    closeControlByModal[modal]();
  };

  // A battery with a damaged barcode is at the hub, so it is counted as a defect battery
  // instead of being outbounded.
  const reportWithMissingBarcode = async (asset: Asset) => {
    try {
      await reportMissingBarcode(asset.id);
      showToastUI({
        status: "success",
        id: "battery_count_success",
        title: "pages.inventory.battery-count-missing-page.update-state-toast.success",
      });
      removeReportedAsset(asset.id);
      addCountedAsset({ ...asset, state: AssetState.asset_state_defect });
      setSelectedReasons((prev) => ({ ...prev, [asset.id]: AssetState.asset_state_defect }));
    } catch {
      showToastUI({
        status: "error",
        id: "battery_count_error",
        title: "pages.inventory.battery-count-missing-page.update-state-toast.error",
      });
    }
  };

  const handleReasonChange = (asset: Asset) => (newReason: AssetState) => {
    const { id: assetId, state: currentAssetState } = asset;
    if (newReason === AssetState.asset_state_defect) {
      // The dropdown fires again for the selected option; don't report the damage twice
      if (selectedReasons[assetId] === AssetState.asset_state_defect) return;
      reportWithMissingBarcode(asset);
    } else if (selectedReasons[assetId] === AssetState.asset_state_defect) {
      // Switching away from a missing barcode skips the confirmation modals
      updateWithNewState(assetId, newReason);
    } else if (
      currentAssetState === AssetState.asset_state_defect &&
      (newReason === AssetState.asset_state_lost || newReason === AssetState.asset_state_stolen)
    ) {
      handleOpenModalToConfirm("damage", assetId, newReason);
    } else if (
      currentAssetState === AssetState.asset_state_operational &&
      (newReason === AssetState.asset_state_lost || newReason === AssetState.asset_state_stolen)
    ) {
      handleOpenModalToConfirm("lost", assetId, newReason);
    } else if (
      currentAssetState === AssetState.asset_state_operational &&
      newReason === AssetState.asset_state_in_repair
    ) {
      handleOpenModalToConfirm("functional", assetId, newReason);
    } else {
      updateWithNewState(assetId, newReason);
    }
  };

  const handleFinish = async () => {
    try {
      await finishRecurrentTask({
        variables: {
          input: {
            count: countedAssets.length,
            recurrentTaskId: currentTaskId!,
          },
        },
      });
      sendSegmentTrackEvent("assetCountStateUpdated", {
        asset_event_type: AssetCountEventType.AssetCountFinished,
        asset_count_process_id: currentTaskId!,
        asset_count_session_id: countSessionId!,
        status: AssetCountStatus.Done,
        asset_type: ASSET_TYPE,
        number_of_asset_counted: countedAssets.length,
      });
      sendSegmentTrackEvent("assetOutboundConfirmed", {
        asset_type: ASSET_TYPE,
        asset_count_process_id: currentTaskId!,
        asset_count_session_id: countSessionId!,
        number_of_outbounded_assets: reportedAssets.length,
        assets: serialiseReportedAssets(reportedAssets),
      });

      showToastUI({
        status: "success",
        id: "battery_count_success",
        title: "pages.inventory.battery-count-missing-page.toast.success",
      });
      resetStore();
      navigate(routes.inventory.batteryCount.root);
    } catch {
      showToastUI({
        status: "error",
        id: "battery_count_error",
        title: "pages.inventory.battery-count-missing-page.toast.error",
      });
    }
  };

  return (
    <Page isCentered isFull isBgGrey justifyContent="flex-start">
      <NavigationHeader
        title={intl.formatMessage({ id: "pages.inventory.battery-count-missing-page.title" })}
        onClickGoBack={() => navigate(routes.inventory.batteryCount.scan)}
      />

      <Box position="relative" w="full" h="full" bg="grey.150" p="s200" overflow="auto">
        <TitleS mb="s200">
          {intl.formatMessage(
            { id: "pages.inventory.battery-count-missing-page.missing-title" },
            { count: missingAssets.length },
          )}
        </TitleS>
        <Flex gap="s200" direction="column">
          {missingAssets.map((battery) => (
            <BatteryReportDropdown
              key={battery.id}
              model={battery.model?.name ?? ""}
              id={battery.serialNumber}
              selectedReason={selectedReasons[battery.id] ?? null}
              setSelectedReason={handleReasonChange(battery)}
            />
          ))}
        </Flex>
      </Box>

      <Box bg="white" w="full" p="s200" mt="auto">
        <Button
          variant="primary"
          isFullWidth
          onClick={handleFinish}
          isDisabled={!allReasonsSelected}
          isLoading={isUpdatingAssetState || isReportingMissingBarcode || isFinishingRecurrentTask}
        >
          {intl.formatMessage({
            id: "pages.inventory.battery-count-missing-page.button.confirm-stock",
          })}
        </Button>
      </Box>

      <BatteryDamagedModal
        isOpen={isBatteryDamagedModalOpen}
        onClose={closeBatteryDamagedModal}
        onClickPrimary={handleModalConfirm("damage")}
        onClickSecondary={handleModalCancel("damage")}
        newStatus={reasonToConfirm!}
      />
      <ReportBatteryLostModal
        isOpen={isReportBatteryLostModalOpen}
        onClose={closeReportBatteryLostModal}
        onClickPrimary={handleModalConfirm("lost")}
        onClickSecondary={handleModalCancel("lost")}
        newStatus={reasonToConfirm!}
      />
      <BatteryFunctionalModal
        isOpen={isBatteryFunctionalModalOpen}
        onClose={closeBatteryFunctionalModal}
        onClickPrimary={handleModalConfirm("functional")}
        onClickSecondary={handleModalCancel("functional")}
      />
    </Page>
  );
}
